import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CatalogIndexRow } from '@nousmedis/cc-types';
import { CatalogIndexService } from '../../../services/catalog-index.service';
import { FascicleService } from '../../../services/fascicle.service';
import { TableLazyLoadEvent } from 'primeng/table';
import { FilterMetadata } from 'primeng/api';

interface LoadParams {
  page: number;
  filters: Record<string, string | undefined>;
  sortField?: string;
  sortOrder?: number;
}

@Component({
  selector: 'app-catalog-index',
  templateUrl: './catalog-index.component.html',
  styleUrls: ['./catalog-index.component.scss'],
})
export class CatalogIndexComponent implements OnInit, OnDestroy {
  idCatalog: string = '';
  catalogName: string = '';
  rows: CatalogIndexRow[] = [];
  totalRecords: number = 0;
  loading: boolean = true;
  pageSize: number = 50;
  fascicleOptions: { label: string; value: string }[] = [];

  private load$ = new Subject<LoadParams>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private catalogIndexService: CatalogIndexService,
    private fascicleService: FascicleService,
  ) {}

  ngOnInit(): void {
    this.idCatalog = this.route.snapshot.paramMap.get('idCatalog') ?? '';

    this.fascicleService.fetch(this.idCatalog).subscribe((fascicles: any[]) => {
      this.fascicleOptions = fascicles
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
        .map(f => ({ label: f.name, value: f.name }));
    });

    this.load$.pipe(
      switchMap(({ page, filters, sortField, sortOrder }) => {
        this.loading = true;
        return this.catalogIndexService.getIndex(this.idCatalog, page, this.pageSize, {
          sku: filters['productSku'],
          productName: filters['productName'],
          fascicle: filters['fascicleName'],
          pageNumber: filters['pageOrder'],
        }, sortField, sortOrder);
      })
    ).subscribe({
      next: (res) => {
        this.rows = res.data;
        this.totalRecords = res.total;
        this.loading = false;
        if (!this.catalogName && res.data.length > 0) {
          this.catalogName = res.data[0].catalogName ?? '';
        }
      },
      error: () => { this.loading = false; },
    });

    this.load$.next({ page: 1, filters: {} });
  }

  ngOnDestroy(): void {
    this.load$.complete();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const first = event.first ?? 0;
    const rows = event.rows ?? this.pageSize;
    this.pageSize = rows;
    const page = Math.floor(first / rows) + 1;

    const filters: Record<string, string | undefined> = {};
    for (const [key, val] of Object.entries(event.filters ?? {})) {
      const meta = Array.isArray(val) ? val[0] : val as FilterMetadata;
      if (meta?.value != null && meta.value !== '') {
        filters[key] = String(meta.value);
      }
    }

    const sortField = event.sortField as string | undefined;
    const sortOrder = event.sortOrder ?? undefined;
    this.load$.next({ page, filters, sortField, sortOrder });
  }

  goToPage(row: CatalogIndexRow): void {
    this.router.navigate([
      '/catalog', row.catalogId, 'fascicle', row.fascicleId,
    ], { queryParams: { page: row.pageId } });
  }

  isEmpty(row: CatalogIndexRow): boolean {
    return !row.productId;
  }
}
